import { useEffect, useState } from 'react';
import { X, CheckCircle, AlertCircle, Info, RefreshCw } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info' | 'update';

interface ToastProps {
  message: string;
  type?: ToastType;
  duration?: number;
  onClose: () => void;
  action?: {
    label: string;
    onClick: () => void;
  };
}

const toastConfig: Record<ToastType, { icon: typeof CheckCircle; bgColor: string; textColor: string }> = {
  success: {
    icon: CheckCircle,
    bgColor: 'bg-success',
    textColor: 'text-success-foreground',
  },
  error: {
    icon: AlertCircle,
    bgColor: 'bg-danger',
    textColor: 'text-white',
  },
  info: {
    icon: Info,
    bgColor: 'bg-primary',
    textColor: 'text-primary-foreground',
  },
  update: {
    icon: RefreshCw,
    bgColor: 'bg-warning',
    textColor: 'text-warning-foreground',
  },
};

export function Toast({ message, type = 'info', duration = 5000, onClose, action }: ToastProps) {
  const [isVisible, setIsVisible] = useState(false);

  const config = toastConfig[type];
  const Icon = config.icon;

  useEffect(() => {
    // Trigger enter animation
    requestAnimationFrame(() => setIsVisible(true));

    if (duration > 0) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(onClose, 300); // Wait for exit animation
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  return (
    <div
      className={`
        fixed bottom-4 right-4 z-50 max-w-sm
        transform transition-all duration-300 ease-out
        ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}
      `}
    >
      <div
        className={`
          flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg
          ${config.bgColor} ${config.textColor}
        `}
      >
        <Icon className="h-5 w-5 flex-shrink-0" />
        <span className="flex-1 text-sm font-medium">{message}</span>
        {action && (
          <button
            onClick={action.onClick}
            className="text-sm font-medium underline hover:no-underline"
          >
            {action.label}
          </button>
        )}
        <button
          onClick={() => {
            setIsVisible(false);
            setTimeout(onClose, 300);
          }}
          className="p-0.5 rounded hover:bg-white/20 transition-colors"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// Hook for managing toasts
interface ToastState {
  id: number;
  message: string;
  type: ToastType;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

let toastId = 0;

export function useToast() {
  const [toasts, setToasts] = useState<ToastState[]>([]);

  const showToast = (
    message: string,
    type: ToastType = 'info',
    options?: { duration?: number; action?: { label: string; onClick: () => void } }
  ) => {
    const id = ++toastId;
    setToasts((prev) => [...prev, { id, message, type, ...options }]);
    return id;
  };

  const hideToast = (id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const ToastContainer = () => (
    <>
      {toasts.map((toast, index) => (
        <div
          key={toast.id}
          style={{ bottom: `${(index * 72) + 16}px` }}
          className="fixed right-4 z-50"
        >
          <Toast
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            action={toast.action}
            onClose={() => hideToast(toast.id)}
          />
        </div>
      ))}
    </>
  );

  return { showToast, hideToast, ToastContainer };
}
